import { defaultLangLocale, LangLocale, verbatim } from '@myre/common-i18n';
import { getLanguagesForCurrentEnv } from '@myre/common-language';
import { compareByLowerCaseFactory } from '@myre/common-primitives';
import { type UiI18n } from '@myre/domain-i18n-wordings';
import { DynamicEnvService } from '@myre/ui-domain-http';
import {
  ActionsDropdownComponent,
  type TextItem,
} from '@myre/ui-primitive-actions-dropdown';
import {
  ButtonComponent,
  ButtonHeight,
  ButtonSubTheme,
  ButtonTheme,
} from '@myre/ui-primitive-components';
import { ElementBase, QuestionDropdown } from '@myre/ui-primitive-forms';
import { ModalService } from '@myre/ui-primitive-modal';
import { TooltipDirective } from '@myre/ui-primitive-tooltip-directive';

import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { AbstractLanguageAnalyticsService } from '../../../analytics/abstract-language-analytics.service';
import {
  getLangCookie,
  setLangCookie,
} from '../../../cookies/lang-cookie.helpers';
import { WORDINGS } from '../../../myre-wordings-loader';
import { AssetsPipe } from '../../../pipes/assets.pipe';

import { generateLegalDropdownItems } from './helpers';
import { LocaleRegionPipe } from './pipes/locale-region.pipe';

enum LogoDeclination {
  Grey = 'minimal-grey',
  Color = 'minimal-colored',
}

@Component({
  selector: 'myre-footer-minimalist',
  templateUrl: './footer-minimalist.component.html',
  styleUrl: './footer-minimalist.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TooltipDirective,
    ButtonComponent,
    ActionsDropdownComponent,
    NgOptimizedImage,
    AssetsPipe,
    LocaleRegionPipe,
  ],
})
export class FooterMinimalistComponent {
  private readonly _cookieService = inject(CookieService);
  private readonly _dynamicEnvService = inject(DynamicEnvService);
  private readonly _modalService = inject(ModalService);

  private readonly _abstractLanguageAnalyticsService = inject(
    AbstractLanguageAnalyticsService,
    { optional: true },
  );

  protected readonly _wordings = inject<UiI18n>(WORDINGS);

  protected readonly ButtonTheme = ButtonTheme;
  protected readonly ButtonSubTheme = ButtonSubTheme;
  protected readonly ButtonHeight = ButtonHeight;
  protected readonly LogoDeclination = LogoDeclination;

  protected readonly _selectedLang = this._getSelectedLang();

  protected readonly _versionApp = DynamicEnvService.VERSION?.version;

  protected readonly _dropdownItems: TextItem[] = generateLegalDropdownItems(
    this._wordings,
  );

  protected _logoName = signal(LogoDeclination.Grey);

  protected _openLanguageModal(): void {
    this._modalService.open({
      key: 'footer.language-select',
      header: {
        data: {
          title: this._wordings._ui.system.manageLanguage(),
        },
      },
      context: {
        elements: [
          new QuestionDropdown<LangLocale>({
            key: 'lang',
            label: this._wordings._ui.system.language(),
            required: true,
            initialValue: this._selectedLang,
            options: getLanguagesForCurrentEnv()
              .map(l => ({
                value: l.langLocale,
                label: verbatim(l.nativeName),
              }))
              .sort(compareByLowerCaseFactory('value')),
            onValueChange: (
              value: LangLocale,
              _: Map<string, ElementBase>,
            ): void => {
              if (value === this._selectedLang) {
                return;
              }
              this._changeLanguage(value);
            },
          }),
        ],
      },
    });
  }

  private _changeLanguage(lang: LangLocale): void {
    this._abstractLanguageAnalyticsService?.trackLanguageChanged({
      language: lang,
    });
    this._setCookie(lang);
    location.reload();
  }

  private _getSelectedLang(): LangLocale {
    return (
      getLangCookie({
        cookieService: this._cookieService,
        cookieSuffix: this._dynamicEnvService.env.cookieSuffix,
      }) || defaultLangLocale
    );
  }

  private _setCookie(lang: string): void {
    setLangCookie({
      lang,
      cookieService: this._cookieService,
      cookieSuffix: this._dynamicEnvService.env.cookieSuffix,
      domain: this._dynamicEnvService.env.domain,
    });
  }
}
